// frontend/src/pages/dashboards/filterState.js
// Phase 4 step 14b-2: the filter bar, as pure functions.
//
// A dashboard's `filters` are its definitions, saved with the dashboard
// (backend app/dashboards/layout.py): { id, label, kind, field, default }.
//   kind 'date'    narrows a date: a relative preset ('last_30_days') or a
//                  range { from?, to? } of ISO days. `field` null means each
//                  chart's own date field (the server finds it).
//   kind 'values'  "is any of" a list picked from the field's values.
//   kind 'text'    "contains" what was typed.
// What a viewer picks is NOT saved: it lives in the URL (?f=), so a link
// carries it, and only what differs from the definitions' defaults is there.
//
// A chart tile may bind a filter to another field of its own dataset, or opt
// out (settings.filter_bindings { filter id: field key | null }). For each
// tile, requestFilters turns the bar into the body of
// POST /semantic/charts/{id}/data; the server ANDs it onto the chart's saved
// spec and reports what did not fit that chart, which notFilteredBy turns
// into the tile's "Not filtered by …".
import { RELATIVE_PRESETS } from '../builder/filters';

export const MAX_FILTERS = 10;          // the server's limits (backend app/dashboards/layout.py)
export const MAX_LABEL = 60;
export const MAX_VALUES = 200;          // spec.MAX_IN_VALUES
export const MAX_TEXT_VALUE = 500;      // spec.MAX_STRING_VALUE
export const MAX_VALUE_SOURCES = 5;     // datasets a values list is gathered from
export const PARAM = 'f';
export const FILTER_KINDS = ['date', 'values', 'text'];
export const KIND_LABELS = { date: 'Date', values: 'List of values', text: 'Text search' };
export { RELATIVE_PRESETS };

const PRESET_LABEL = Object.fromEntries(RELATIVE_PRESETS);
const PRESETS = new Set(RELATIVE_PRESETS.map(([k]) => k));
const ID = /^[A-Za-z0-9_-]{1,40}$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const CONTROL = /[\x00-\x08\x0b-\x1f\x7f]/g; // eslint-disable-line no-control-regex
const DATE_TYPES = ['date', 'datetime'];
const LISTABLE_TYPES = ['string', 'integer', 'boolean'];      // what the values route lists (field_values.LISTABLE_TYPES)

export const presetLabel = (preset) => PRESET_LABEL[preset] || preset;

// --- definitions ------------------------------------------------------------------

let counter = 0;
/** A new filter id, unique among `existing` definitions. */
export function newFilterId(existing = []) {
  const taken = new Set(existing.map((d) => d.id));
  let id;
  do { counter += 1; id = `f${counter}${Math.random().toString(36).slice(2, 6)}`; } while (taken.has(id));
  return id;
}

/** A label as the server keeps it: one line, no control characters, trimmed, within MAX_LABEL. */
export function cleanLabel(value) {
  const s = String(value ?? '').replace(CONTROL, '').replace(/[\r\n]+/g, ' ').trim();
  return s.length > MAX_LABEL ? s.slice(0, MAX_LABEL) : s;
}

/** The server's definitions, kept only when well-formed (an unknown kind is dropped, not guessed). */
export function definitionsFromServer(list) {
  return (Array.isArray(list) ? list : [])
    .filter((d) => d && ID.test(String(d.id || '')) && FILTER_KINDS.includes(d.kind))
    .map((d) => ({
      id: d.id, label: d.label || 'Filter', kind: d.kind, field: d.field ?? null,
      default: cleanValue(d, d.default) ?? null,
    }));
}

/** One definition as the PUT body wants it. */
export const definitionToSave = (d) => ({ id: d.id, label: cleanLabel(d.label), kind: d.kind, field: d.field ?? null, default: d.default ?? null });

/** True when two definition lists would save the same thing (order matters: it is the bar's order). */
export function sameDefinitions(a, b) {
  return JSON.stringify((a || []).map(definitionToSave)) === JSON.stringify((b || []).map(definitionToSave));
}

/** What is wrong with a definition in the editor, or null. */
export function definitionProblem(def, others = []) {
  if (!cleanLabel(def.label)) return 'Give the filter a name.';
  if (String(def.label).trim().length > MAX_LABEL) return `Keep the name under ${MAX_LABEL} characters.`;
  if (!FILTER_KINDS.includes(def.kind)) return 'Choose what the filter narrows.';
  if (def.kind !== 'date' && !def.field) return 'Choose a field.';
  const label = cleanLabel(def.label).toLowerCase();
  if (others.some((o) => o.id !== def.id && cleanLabel(o.label).toLowerCase() === label)) return 'Another filter has this name.';
  return null;
}

/** The fields a kind of filter can narrow, from a field list ({ key, data_type, ... }). */
export function fieldsForKind(kind, fields) {
  const ok = kind === 'date' ? DATE_TYPES : kind === 'values' ? LISTABLE_TYPES : kind === 'text' ? ['string'] : [];
  return (fields || []).filter((f) => f && ok.includes(f.data_type));
}

/**
 * The fields offered when defining a filter: every chartable field of the
 * dashboard's datasets, merged by key, with how many of those datasets have
 * it. `fieldLists` is [[field, ...] per dataset]. Most widely shared first.
 */
export function fieldUnion(fieldLists) {
  const byKey = new Map();
  for (const list of fieldLists || []) {
    const seen = new Set();
    for (const f of list || []) {
      if (!f || !f.key || seen.has(f.key) || f.role === 'ignore' || f.data_type === 'json_array' || f.data_type === 'json_object') continue;
      seen.add(f.key);
      const had = byKey.get(f.key);
      if (had) had.datasets += 1;
      else byKey.set(f.key, { key: f.key, display_name: f.display_name || f.key, data_type: f.data_type, datasets: 1 });
    }
  }
  return [...byKey.values()].sort((a, b) => b.datasets - a.datasets || a.display_name.localeCompare(b.display_name));
}

// --- values -----------------------------------------------------------------------

function realDay(s) {
  if (typeof s !== 'string' || !ISO_DAY.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

const scalar = (v) => (typeof v === 'string' && v.length <= MAX_TEXT_VALUE) || typeof v === 'boolean' || (typeof v === 'number' && Number.isFinite(v));

/**
 * A value for this definition, cleaned; null for "nothing picked"; undefined
 * for something that is not a value of this kind at all (a hand-edited URL).
 */
export function cleanValue(def, raw) {
  if (raw === null || raw === undefined) return null;
  if (def.kind === 'date') {
    if (typeof raw === 'string') return PRESETS.has(raw) ? raw : undefined;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
    const out = {};
    if (raw.from != null) { if (!realDay(raw.from)) return undefined; out.from = raw.from; }
    if (raw.to != null) { if (!realDay(raw.to)) return undefined; out.to = raw.to; }
    if (!out.from && !out.to) return null;
    if (out.from && out.to && out.from > out.to) return undefined;
    return out;
  }
  if (def.kind === 'values') {
    if (!Array.isArray(raw)) return undefined;
    if (!raw.every(scalar)) return undefined;
    const seen = new Set();
    const out = raw.filter((v) => { const k = `${typeof v}:${v}`; if (seen.has(k)) return false; seen.add(k); return true; });
    if (out.length > MAX_VALUES) return undefined;
    return out.length ? out : null;
  }
  if (def.kind === 'text') {
    if (typeof raw !== 'string') return undefined;
    const s = raw.replace(CONTROL, '').replace(/[\r\n]+/g, ' ').trim();
    if (s.length > MAX_TEXT_VALUE) return undefined;
    return s || null;
  }
  return undefined;
}

export const isActive = (value) => value !== null && value !== undefined;

/**
 * The bar's current values: { filter id: value | null }. `overrides` are
 * what the URL says ({ id: value | null }); a definition without an override
 * shows its default. An override of null means "cleared", even with a default.
 */
export function effectiveValues(defs, overrides = {}) {
  const out = {};
  for (const d of defs || []) {
    if (Object.prototype.hasOwnProperty.call(overrides, d.id)) {
      const v = cleanValue(d, overrides[d.id]);
      out[d.id] = v === undefined ? (d.default ?? null) : v;
    } else out[d.id] = d.default ?? null;
  }
  return out;
}

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The overrides after the viewer sets one filter: only what differs from its default is kept. */
export function setOverride(defs, overrides, id, value) {
  const def = (defs || []).find((d) => d.id === id);
  const next = { ...overrides };
  if (!def) { delete next[id]; return next; }
  const v = cleanValue(def, value);
  if (v === undefined || same(v, def.default)) delete next[id];
  else next[id] = v;
  return next;
}

/** True when any filter shows something other than its default (the bar offers "Reset"). */
export function hasOverrides(defs, overrides) {
  const vals = effectiveValues(defs, overrides);
  return (defs || []).some((d) => !same(vals[d.id], d.default));
}

/** The ?f= value for these overrides: '' when there is nothing to carry. */
export function encodeOverrides(defs, overrides) {
  const out = {};
  for (const d of defs || []) {
    if (!Object.prototype.hasOwnProperty.call(overrides || {}, d.id)) continue;
    const v = cleanValue(d, overrides[d.id]);
    if (v === undefined || same(v, d.default)) continue;
    out[d.id] = v;
  }
  return Object.keys(out).length ? JSON.stringify(out) : '';
}

/** The overrides a ?f= value carries, keeping only well-formed values of filters this dashboard has. */
export function decodeOverrides(param, defs) {
  if (!param) return {};
  let raw;
  try { raw = JSON.parse(param); } catch { return {}; }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const d of defs || []) {
    if (!Object.prototype.hasOwnProperty.call(raw, d.id)) continue;
    const v = cleanValue(d, raw[d.id]);
    if (v !== undefined) out[d.id] = v;
  }
  return out;
}

// --- per tile ---------------------------------------------------------------------

/** A tile's binding for a filter: undefined (the filter's own field), null (left out), or a field key. */
export function bindingFor(tile, defId) {
  const b = tile && tile.settings && tile.settings.filter_bindings;
  if (!b || !Object.prototype.hasOwnProperty.call(b, defId)) return undefined;
  return b[defId] === null ? null : b[defId];
}

/** The tiles with one tile's binding for one filter set (undefined removes it). */
export function withBinding(tiles, key, defId, binding) {
  return tiles.map((t) => {
    if (t.key !== key) return t;
    const bindings = { ...((t.settings && t.settings.filter_bindings) || {}) };
    if (binding === undefined) delete bindings[defId];
    else bindings[defId] = binding;
    const settings = { ...t.settings };
    if (Object.keys(bindings).length) settings.filter_bindings = bindings;
    else delete settings.filter_bindings;
    return { ...t, settings };
  });
}

/** The tiles without bindings to filters that are not in `defs` (a filter was removed). */
export function pruneBindings(tiles, defs) {
  const ids = new Set((defs || []).map((d) => d.id));
  return tiles.map((t) => {
    const b = t.settings && t.settings.filter_bindings;
    if (!b || Object.keys(b).every((id) => ids.has(id))) return t;
    const kept = Object.fromEntries(Object.entries(b).filter(([id]) => ids.has(id)));
    const settings = { ...t.settings };
    if (Object.keys(kept).length) settings.filter_bindings = kept;
    else delete settings.filter_bindings;
    return { ...t, settings };
  });
}

/** The ISO day after `iso` (a range "to" is inclusive: < the next day works for dates and timestamps alike). */
export function nextDay(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The body's filters for one chart tile: { filters, owners, optedOut }.
 * `owners[i]` is the definition id behind filters[i] (a date range is two
 * items); `optedOut` the active filters this tile was bound out of.
 */
export function requestFilters(defs, values, tile) {
  const filters = [];
  const owners = [];
  const optedOut = [];
  for (const d of defs || []) {
    const value = values ? values[d.id] : null;
    if (!isActive(value)) continue;
    const binding = bindingFor(tile, d.id);
    if (binding === null) { optedOut.push(d.id); continue; }
    const field = binding !== undefined ? binding : d.field;
    const push = (item) => { filters.push(item); owners.push(d.id); };
    if (d.kind === 'date') {
      const at = field ? { field } : { target: 'time' };
      if (typeof value === 'string') push({ ...at, op: 'relative', value });
      else {
        if (value.from) push({ ...at, op: 'gte', value: value.from });
        if (value.to) push({ ...at, op: 'lt', value: nextDay(value.to) });
      }
    } else if (!field) {
      continue;                                       // a values/text filter always has a field; nothing to send without one
    } else if (d.kind === 'values') {
      push({ field, op: 'in', value });
    } else if (d.kind === 'text') {
      push({ field, op: 'contains', value });
    }
  }
  return { filters, owners, optedOut };
}

/** The labels of the active filters that do not narrow this tile, in the bar's order. */
export function notFilteredBy(defs, owners, narrowing, optedOut = []) {
  const ids = new Set(optedOut);
  for (const s of (narrowing && narrowing.skipped) || []) {
    const id = owners[s.index];
    if (id) ids.add(id);
  }
  return (defs || []).filter((d) => ids.has(d.id)).map((d) => d.label);
}

/** "Not filtered by Period", "Not filtered by Period or Region", "Not filtered by 3 filters". */
export function notFilteredText(labels) {
  if (!labels || !labels.length) return '';
  if (labels.length === 1) return `Not filtered by ${labels[0]}`;
  if (labels.length === 2) return `Not filtered by ${labels[0]} or ${labels[1]}`;
  return `Not filtered by ${labels.length} filters`;
}

// --- the bar's words --------------------------------------------------------------

function day(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

const shown = (v) => (typeof v === 'boolean' ? (v ? 'True' : 'False') : v === '' ? '(empty)' : String(v));

/** What a filter's button says about its value. */
export function valueSummary(def, value) {
  if (!isActive(value)) return 'All';
  if (def.kind === 'date') {
    if (typeof value === 'string') return presetLabel(value);
    if (value.from && value.to) return value.from === value.to ? day(value.from) : `${day(value.from)} – ${day(value.to)}`;
    return value.from ? `From ${day(value.from)}` : `Until ${day(value.to)}`;
  }
  if (def.kind === 'values') {
    if (value.length === 1) return shown(value[0]);
    if (value.length === 2) return `${shown(value[0])}, ${shown(value[1])}`;
    return `${shown(value[0])} +${value.length - 1}`;
  }
  return `contains “${value}”`;
}

/**
 * The editor's choices for a definition's default: nothing, the default it
 * has, what the bar shows now, and (for a date) each preset.
 * [{ id, label, value }]; ids are stable for a select.
 */
export function defaultChoices(def, current) {
  const out = [{ id: 'none', label: 'Nothing (show everything)', value: null }];
  const add = (id, label, value) => { if (!out.some((o) => same(o.value, value))) out.push({ id, label, value }); };
  if (isActive(def.default)) add('keep', `${valueSummary(def, def.default)}`, def.default);
  const cur = cleanValue(def, current);
  if (isActive(cur)) add('current', `${valueSummary(def, cur)} (what the bar shows now)`, cur);
  if (def.kind === 'date') for (const [k, label] of RELATIVE_PRESETS) add(`p:${k}`, label, k);
  return out;
}

// --- where a values list comes from -------------------------------------------------

/**
 * The (dataset, field) pairs a values filter's list is gathered from: each
 * chart tile it narrows, with the tile's binding applied; unique, at most
 * MAX_VALUE_SOURCES.
 */
export function valueSources(def, tiles) {
  const out = [];
  const seen = new Set();
  for (const t of tiles || []) {
    if (t.kind !== 'chart' || !t.chart || !t.chart.dataset_id) continue;
    const binding = bindingFor(t, def.id);
    if (binding === null) continue;
    const field = binding !== undefined ? binding : def.field;
    if (!field) continue;
    const k = `${t.chart.dataset_id}\u0000${field}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ datasetId: t.chart.dataset_id, field });
    if (out.length >= MAX_VALUE_SOURCES) break;
  }
  return out;
}

/** Several values lists as one: unique, sorted, at most MAX_VALUES; `truncated` when any was or this is. */
export function mergeValues(results) {
  const seen = new Map();
  let truncated = false;
  for (const r of results || []) {
    if (!r) continue;
    if (r.truncated) truncated = true;
    for (const v of r.values || []) {
      if (!scalar(v)) continue;
      const k = `${typeof v}:${v}`;
      if (!seen.has(k)) seen.set(k, v);
    }
  }
  const values = [...seen.values()].sort((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  });
  if (values.length > MAX_VALUES) truncated = true;
  return { values: values.slice(0, MAX_VALUES), truncated };
}

/** The datasets of the dashboard's chart tiles, for the field lists the editor offers. */
export function datasetIds(tiles) {
  return [...new Set((tiles || []).filter((t) => t.kind === 'chart' && t.chart && t.chart.dataset_id).map((t) => t.chart.dataset_id))];
}
